/**
 * Filter-aware ticket count for the agent inbox subtitle.
 *
 * Wraps GET /api/v1/agent/tickets/count which mirrors the list
 * endpoint's filter args and returns a single ``{ total: int }``.
 * Drives the "N tickets" subtitle on AgentInboxPage so that the
 * displayed count ALWAYS matches what the list below it is showing
 * (modulo pagination - the list paginates at 50, the count is the
 * true total).
 *
 * Greg's 2026-05-21 ask: the prior subtitle showed "X open across
 * all tickets, Y assigned to you" regardless of the active filter,
 * which was confusing when the visible rows were a smaller filtered
 * subset.
 *
 * Polls on the same 30s heartbeat as ``useAgentTickets`` so the
 * subtitle stays in sync with the row list and the sidebar badges.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { UseAgentTicketsArgs } from "@/hooks/useAgentTickets";

export interface FilteredCountResponse {
  total: number;
}

const AGENT_INBOX_POLL_MS = 30_000;

/** Cache key for the filter-aware count. Shape mirrors
 * ``agentTicketsQueryKey`` so a future mutation that needs to
 * invalidate "everything that depends on this filter set" can
 * iterate both. */
export function agentFilteredTicketCountQueryKey(
  args: UseAgentTicketsArgs,
): readonly unknown[] {
  return [
    "agent",
    "tickets",
    "filtered-count",
    args.status ?? "default",
    args.assignee ?? "all",
    args.priority ?? "all",
    args.type ?? "all",
    args.q ?? "",
    args.companyId ?? "",
    args.brandId ?? "",
    args.tag ?? "",
    args.deletedOnly ? "deleted" : "live",
    args.view ?? "no-view",
  ] as const;
}

export function useAgentFilteredTicketCount(
  args: UseAgentTicketsArgs = {},
): UseQueryResult<FilteredCountResponse> {
  return useQuery({
    queryKey: agentFilteredTicketCountQueryKey(args),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (args.status) params.status = args.status;
      if (args.assignee) params.assignee = args.assignee;
      if (args.priority) params.priority = args.priority;
      if (args.type) params.type = args.type;
      if (args.q && args.q.length >= 2) params.q = args.q;
      if (args.companyId) params.company_id = args.companyId;
      if (args.brandId) params.brand_id = args.brandId;
      if (args.tag) params.tag = args.tag;
      if (args.deletedOnly) params.deleted_only = "true";
      if (args.view) params.view = args.view;
      const response = await api.get<FilteredCountResponse>(
        "/agent/tickets/count",
        { params },
      );
      return response.data;
    },
    refetchInterval: AGENT_INBOX_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
